import { Component, OnInit } from '@angular/core';
import { SubSink } from 'subsink';
import { AutoUnsubscribe } from '../../../core/auto-unsubscribe';
import { RequestTypeEnum } from '../../../shared/xatena-api/request-type';
import { RequestTypeBaseComponent } from '../request-type-base/request-type-base.component';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { DemandRequestDetailService } from '../demand-request-detail.service';
import { of } from 'rxjs';
import { RequestDto } from '../../../xatena-api';
import { FormlyModule } from '@ngx-formly/core';
import { AsyncPipe } from '@angular/common';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';

@AutoUnsubscribe()
@Component({
  selector: 'app-request-type-human-resource-requirement',
  templateUrl: './request-type-human-resource-requirement.component.html',
  styleUrls: ['./request-type-human-resource-requirement.component.scss'],
  imports: [FormlyModule, AsyncPipe, MatDatepickerModule, MatNativeDateModule],
})
export class RequestTypeHumanResourceRequirementComponent
  extends RequestTypeBaseComponent
  implements OnInit
{
  subs = new SubSink();

  constructor(
    protected translateService: TranslateService,
    demandRequestDetailService: DemandRequestDetailService
  ) {
    super(demandRequestDetailService);
  }

  public get staffRequirementReasonSelectList$() {
    const options: {
      key: number;
      value: string;
    }[] = [
      {
        key: 0,
        value: this.translateService.instant(
          'SHARED.STAFF_REQUIREMENT_REASONS_SELECT.REPLACEMENT'
        ),
      },
      {
        key: 1,
        value: this.translateService.instant(
          'SHARED.STAFF_REQUIREMENT_REASONS_SELECT.NEW_HIRE'
        ),
      },
      {
        key: 2,
        value: this.translateService.instant(
          'SHARED.STAFF_REQUIREMENT_REASONS_SELECT.ADDITIONAL_HIRE'
        ),
      },
      {
        key: 3,
        value: this.translateService.instant(
          'SHARED.STAFF_REQUIREMENT_REASONS_SELECT.REDUCED_DEMAND'
        ),
      },
      {
        key: 4,
        value: this.translateService.instant(
          'SHARED.STAFF_REQUIREMENT_REASONS_SELECT.RELOCATION'
        ),
      },
      {
        key: 5,
        value: this.translateService.instant(
          'SHARED.STAFF_REQUIREMENT_REASONS_SELECT.RECLASSIFICATION'
        ),
      },
      {
        key: 6,
        value: this.translateService.instant(
          'SHARED.STAFF_REQUIREMENT_REASONS_SELECT.SALARY_INCREASE'
        ),
      },
    ];

    return of(options);
  }

  public get staffRequirementImpactSelectList$() {
    const options: {
      key: number;
      value: string;
    }[] = [
      {
        key: 0,
        value: this.translateService.instant(
          'SHARED.STAFF_REQUIREMENT_IMPACT_SELECT.CAPACITY_RETENTION'
        ),
      },
      {
        key: 1,
        value: this.translateService.instant(
          'SHARED.STAFF_REQUIREMENT_IMPACT_SELECT.CAPACITY_EXPANSION'
        ),
      },
      {
        key: 2,
        value: this.translateService.instant(
          'SHARED.STAFF_REQUIREMENT_IMPACT_SELECT.OTHER'
        ),
      },
    ];

    return of(options);
  }

  protected updateTotalCost(request: RequestDto) {
    const staffRequirementCostTotal =
      +(request.staffRequirementCost ?? 0) -
      +(request.staffRequirementSaving ?? 0);

    this.form
      .get('staffRequirementCostTotal')
      ?.setValue(staffRequirementCostTotal, {
        emitEvent: false,
      });
  }

  ngOnInit(): void {
    this.subs.sink = this.form.valueChanges.subscribe(s => {
      this.updateTotalCost(s);
    });

    this.fields = [
      {
        hooks: {
          onInit: field => {
            this.subs.sink = this.isReadOnly$.subscribe(s => {
              if (s) {
                this.form.disable();
              } else {
                this.form.enable();
              }
            });
          },
        },
        hideExpression: model =>
          !(model.requestTypeId === RequestTypeEnum.HumanResourceRequirement),
        fieldGroup: [
          {
            wrappers: ['mat-card'],
            templateOptions: {
              label: _('DEMAND_REQUEST.DETAIL.FORM.REQUEST_TYPE_9_GRP_1.LABEL'),
              translate: true,
            },
            fieldGroup: [
              {
                key: 'employmentTitle',
                type: 'input',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.EMPLOYMENT_TITLE.LABEL'),
                  translate: true,
                },
              },
              {
                key: 'department',
                type: 'input',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.DEPARTMENT.LABEL'),
                  translate: true,
                },
              },
              {
                key: 'responsibilities',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.RESPONSIBILITIES.LABEL'),
                  description: _(
                    'DEMAND_REQUEST.DETAIL.FORM.RESPONSIBILITIES.DESCRIPTION'
                  ),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
              {
                key: 'staffRequirementReasonType',
                type: 'select',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.STAFF_REQUIREMENT_REASON_TYPE.LABEL'
                  ),
                  translate: true,
                  options: this.staffRequirementReasonSelectList$,
                  valueProp: 'key',
                  labelProp: 'value',
                },
              },
              {
                key: 'staffRequirementReason',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.STAFF_REQUIREMENT_REASON.LABEL'
                  ),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
            ],
          },
          {
            wrappers: ['mat-card'],
            templateOptions: {
              label: _('DEMAND_REQUEST.DETAIL.FORM.REQUEST_TYPE_9_GRP_2.LABEL'),
              translate: true,
            },
            fieldGroup: [
              {
                key: 'staffRequirementGoals',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.STAFF_REQUIREMENT_GOALS.LABEL'
                  ),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
              {
                key: 'staffRequirementMeasurements',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.STAFF_REQUIREMENT_MEASUREMENTS.LABEL'
                  ),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
              {
                key: 'staffRequirementConsequences',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.STAFF_REQUIREMENT_CONSEQUENCES.LABEL'
                  ),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
              {
                key: 'staffRequirementImpactType',
                type: 'select',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.STAFF_REQUIREMENT_IMPACT_TYPE.LABEL'
                  ),
                  translate: true,
                  options: this.staffRequirementImpactSelectList$,
                  valueProp: 'key',
                  labelProp: 'value',
                },
              },
              {
                key: 'staffRequirementImpactReason',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.STAFF_REQUIREMENT_IMPACT_REASON.LABEL'
                  ),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
            ],
          },
          {
            wrappers: ['mat-card'],
            templateOptions: {
              label: _('DEMAND_REQUEST.DETAIL.FORM.REQUEST_TYPE_9_GRP_3.LABEL'),
              translate: true,
            },
            fieldGroup: [
              {
                key: 'staffRequirementCostConsequences',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.STAFF_REQUIREMENT_COST_CONSEQUENCES.LABEL'
                  ),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
              {
                key: 'staffRequirementSaving',
                type: 'currency',
                templateOptions: {
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.STAFF_REQUIREMENT_SAVING.LABEL'
                  ),
                  translate: true,
                  currency: this.currency$,
                },
              },
              {
                key: 'staffRequirementCost',
                type: 'currency',
                templateOptions: {
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.STAFF_REQUIREMENT_COST.LABEL'
                  ),
                  translate: true,
                  currency: this.currency$,
                },
              },
              {
                key: 'staffRequirementCostTotal',
                type: 'currency',
                templateOptions: {
                  readonly: true,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.STAFF_REQUIREMENT_COST_TOTAL.LABEL'
                  ),
                  translate: true,
                  currency: this.currency$,
                },
              },
              {
                key: 'staffRequirementEquipment',
                type: 'textarea',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.STAFF_REQUIREMENT_EQUIPMENT.LABEL'
                  ),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
              {
                key: 'staffRequirementActivation',
                type: 'datepicker',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.STAFF_REQUIREMENT_ACTIVATION.LABEL'
                  ),
                  translate: true,
                },
              },
              {
                key: 'staffRequirementOtherComments',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.STAFF_REQUIREMENT_OTHER_COMMENTS.LABEL'
                  ),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
            ],
          },
        ],
      },
    ];
  }
}
